import { X } from "lucide-mui"
import React from "react"
import { useLocation, useNavigate } from "react-router-dom"

import VenueFooterDesktop from "@/components/public/venue/venue_footer/desktop"
import i18n from "@/i18n"
import PublicTopBar from "@/pages/public/top_bar"
import useIsMobile from "@/shared/use_is_mobile"

import notFoundIllustration from "./not_found_illustration.svg"

const STYLES = {
  page: "min-h-screen bg-[#565858] font-['Inter','Roboto',sans-serif] text-[#171717]",
  desktopPage: "flex min-h-screen flex-col bg-white font-['Inter','Roboto',sans-serif] text-[#171717]",
  desktopMain: "flex flex-1 items-center justify-center bg-white px-6 pb-[70px] pt-[72px]",
  content: "flex flex-col items-center text-center",
  desktopContent: "gap-0",
  mobileContent: "mt-[142px]",
  illustration: "h-[176px] w-[171px]",
  title: "mt-10 text-[30px] font-bold leading-[36px] tracking-[-0.01em] text-[#171717]",
  message: "mt-5 max-w-[273px] text-[18px] font-normal leading-[22px] text-[#171717] md:max-w-none md:leading-[22px]",
  backButton:
    "mt-[30px] min-w-[100px] rounded-[10px] bg-[#171717] px-[14px] py-[7px] text-[18px] font-medium leading-[22px] text-white transition-colors hover:bg-[#2a2a2a] focus:outline-none focus:ring-2 focus:ring-[#171717] focus:ring-offset-2",
  mobilePanel: "min-h-[calc(100vh-60px)] rounded-t-[20px] bg-white px-5 pt-5",
  mobileTopRow: "flex justify-end",
  closeButton:
    "flex h-6 w-6 items-center justify-center rounded-[3px] bg-[#f1f1f0] text-[#343434] transition-colors hover:bg-[#e5e5e4] focus:outline-none focus:ring-2 focus:ring-[#171717] focus:ring-offset-2",
  closeIcon: "h-[18px] w-[18px]",
} as const

type NotFoundContentProps = {
  onBack: () => void
  variant: "desktop" | "mobile"
}

const NotFoundContent: React.FC<NotFoundContentProps> = ({ onBack, variant }) => (
  <section className={`${STYLES.content} ${variant === "mobile" ? STYLES.mobileContent : STYLES.desktopContent}`}>
    <img
      src={notFoundIllustration}
      alt=""
      role="presentation"
      className={STYLES.illustration}
      data-testid="not-found-illustration"
    />
    <h1 className={STYLES.title}>{i18n.t("app.errors.not_found_title")}</h1>
    <p className={STYLES.message} data-testid="auth-not-found-message">
      {i18n.t("app.errors.not_found")}
    </p>
    <button type="button" onClick={onBack} data-testid="auth-not-found-back-btn" className={STYLES.backButton}>
      {i18n.t("app.errors.back")}
    </button>
  </section>
)

const NotFound: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const handleBack = () => {
    if (location?.key && location.key !== "default") {
      navigate(-1)
      return
    }

    navigate("/")
  }

  if (isMobile) {
    return (
      <div className={STYLES.page} data-testid="auth-not-found-page">
        <PublicTopBar />
        <main className={STYLES.mobilePanel}>
          <div className={STYLES.mobileTopRow}>
            <button
              type="button"
              onClick={handleBack}
              aria-label={i18n.t("app.actions.close")}
              className={STYLES.closeButton}
            >
              <X className={STYLES.closeIcon} />
            </button>
          </div>
          <NotFoundContent onBack={handleBack} variant="mobile" />
        </main>
      </div>
    )
  }

  return (
    <div className={STYLES.desktopPage} data-testid="auth-not-found-page">
      <PublicTopBar />
      <main className={STYLES.desktopMain}>
        <NotFoundContent onBack={handleBack} variant="desktop" />
      </main>
      <VenueFooterDesktop />
    </div>
  )
}

export default NotFound
